// src/features/medicine-inventory/components/stock/StockMovementList.jsx
import React, { useState } from 'react';
import { 
  FaPlus, 
  FaArrowDown, 
  FaArrowUp, 
  FaPrescription, 
  FaChevronDown, 
  FaChevronUp,
  FaShoppingCart,
  FaFileInvoice
} from 'react-icons/fa';
import { useInventory } from '../../context/InventoryContext';
import { formatCurrency, formatDate } from '../../utils/helpers';
import Modal from '../common/Modal';
import StockMovementForm from './StockMovementForm';
import MultiMedicineIssueForm from './MultiMedicineIssueForm';
import MultiMedicinePurchaseForm from './MultiMedicinePurchaseForm';

const StockMovementList = () => {
  const { stockMovements } = useInventory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('SINGLE');
  const [filter, setFilter] = useState('ALL');
  const [expandedMovements, setExpandedMovements] = useState({});

  const handleAddMovement = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalType('SINGLE');
  };

  const toggleExpand = (id) => {
    setExpandedMovements(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredMovements = filter === 'ALL' 
    ? stockMovements 
    : stockMovements.filter(m => {
        if (filter === 'PURCHASE') return m.type === 'PURCHASE' || m.type === 'MULTI_PURCHASE';
        if (filter === 'ISSUE') return m.type === 'ISSUE' || m.type === 'MULTI_ISSUE';
        return true;
      });

  const getMovementIcon = (type) => {
    switch(type) {
      case 'PURCHASE':
        return <FaArrowDown className="text-green-500" />;
      case 'MULTI_PURCHASE':
        return <FaShoppingCart className="text-blue-500" />;
      case 'MULTI_ISSUE':
        return <FaPrescription className="text-purple-500" />;
      default:
        return <FaArrowUp className="text-red-500" />;
    }
  };

  const getMovementBadge = (type) => {
    switch(type) {
      case 'PURCHASE':
        return 'bg-green-100 text-green-800';
      case 'MULTI_PURCHASE':
        return 'bg-blue-100 text-blue-800';
      case 'MULTI_ISSUE':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  const getMovementLabel = (type) => {
    switch(type) {
      case 'PURCHASE': 
        return 'Single Purchase';
      case 'MULTI_PURCHASE': 
        return 'Multi-Medicine Purchase';
      case 'MULTI_ISSUE': 
        return 'Multi-Medicine Issue';
      default: 
        return 'Single Issue';
    }
  };

  const renderMovementDetails = (movement) => {
    if (movement.type === 'MULTI_PURCHASE') {
      return (
        <div>
          <button
            onClick={() => toggleExpand(movement.id)}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
          >
            <span>{movement.medicines?.length || 0} medicines</span>
            {expandedMovements[movement.id] ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      );
    } else if (movement.type === 'MULTI_ISSUE') {
      return (
        <div>
          <button
            onClick={() => toggleExpand(movement.id)}
            className="flex items-center space-x-1 text-purple-600 hover:text-purple-800"
          >
            <span>{movement.medicines?.length || 0} medicines</span>
            {expandedMovements[movement.id] ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      );
    } else {
      return (
        <div>
          <div className="font-medium text-gray-900">{movement.medicineName}</div>
          <div className="text-sm text-gray-500">Quantity: {movement.quantity}</div>
        </div>
      );
    }
  };

  const renderItemsCount = (movement) => {
    if (movement.type === 'MULTI_PURCHASE' || movement.type === 'MULTI_ISSUE') {
      return (
        <div className="text-sm text-gray-600">
          {movement.medicines?.length || 0} item(s)
        </div>
      );
    } else {
      return (
        <div className="text-sm text-gray-600">
          {movement.quantity} {movement.unit || 'units'}
        </div>
      );
    }
  };

  const renderTotalAmount = (movement) => {
    if (movement.type === 'MULTI_PURCHASE') {
      return formatCurrency(movement.summary?.total || movement.totalAmount);
    } else if (movement.type === 'MULTI_ISSUE') {
      return formatCurrency(movement.totalAmount);
    } else {
      return formatCurrency(movement.totalPrice);
    }
  };

  const renderReferenceInfo = (movement) => {
    if (movement.type === 'MULTI_PURCHASE') {
      return (
        <div>
          <div className="text-sm font-medium flex items-center">
            <FaFileInvoice className="mr-1 text-gray-400 text-xs" />
            PO: {movement.purchaseOrderNumber}
          </div>
          <div className="text-xs text-gray-500">
            Supplier: {movement.supplierName}
          </div>
          {movement.invoiceNumber && (
            <div className="text-xs text-gray-500">
              Invoice: {movement.invoiceNumber}
            </div>
          )}
          {movement.notes && (
            <div className="text-xs text-gray-500 mt-1">{movement.notes}</div>
          )}
        </div>
      );
    } else if (movement.type === 'PURCHASE') {
      return (
        <div>
          <div className="text-sm font-medium">PO: {movement.reference}</div>
          {movement.notes && (
            <div className="text-xs text-gray-500">{movement.notes}</div>
          )}
        </div>
      );
    } else if (movement.type === 'MULTI_ISSUE') {
      return (
        <div>
          <div className="text-sm font-medium">{movement.patientName}</div>
          {movement.prescriptionNumber && (
            <div className="text-xs text-gray-500">
              Rx: {movement.prescriptionNumber}
              {movement.doctorName && ` | Dr. ${movement.doctorName}`}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div>
          <div className="text-sm font-medium">{movement.patientName}</div>
          {movement.prescriptionNumber && (
            <div className="text-xs text-gray-500">
              Rx: {movement.prescriptionNumber}
              {movement.doctorName && ` | Dr. ${movement.doctorName}`}
            </div>
          )}
        </div>
      );
    }
  };

  const renderExpandedContent = (movement) => {
    if (movement.type === 'MULTI_PURCHASE') {
      return (
        <tr className="bg-gray-50">
          <td colSpan="6" className="px-6 py-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <FaShoppingCart className="mr-2 text-blue-600" />
                Purchased Medicines:
              </h4>
              
              {/* Purchase Summary */}
              {movement.summary && (
                <div className="bg-white rounded-lg p-3 mb-3 text-sm">
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <span className="text-gray-500">Subtotal:</span>
                      <span className="ml-2 font-medium">{formatCurrency(movement.summary.subtotal)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Discount:</span>
                      <span className="ml-2 font-medium text-red-600">-{formatCurrency(movement.summary.discount)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Tax:</span>
                      <span className="ml-2 font-medium">+{formatCurrency(movement.summary.tax)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 font-bold">Total:</span>
                      <span className="ml-2 font-bold text-blue-600">{formatCurrency(movement.summary.total)}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-600 border-b">
                    <th className="text-left py-2">Medicine</th>
                    <th className="text-left py-2">Quantity</th>
                    <th className="text-left py-2">Unit Price</th>
                    <th className="text-left py-2">Discount</th>
                    <th className="text-left py-2">Tax</th>
                    <th className="text-left py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {movement.medicines.map((med, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="py-2">
                        <div className="font-medium">{med.medicineName}</div>
                        {med.batchNumber && (
                          <div className="text-xs text-gray-500">Batch: {med.batchNumber}</div>
                        )}
                        {med.expiryDate && (
                          <div className="text-xs text-gray-500">Exp: {formatDate(med.expiryDate)}</div>
                        )}
                      </td>
                      <td className="py-2">{med.quantity}</td>
                      <td className="py-2">{formatCurrency(med.unitPrice)}</td>
                      <td className="py-2">{med.discount}%</td>
                      <td className="py-2">{med.taxRate}%</td>
                      <td className="py-2 font-medium">{formatCurrency(med.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Payment Status */}
              {movement.paymentStatus && (
                <div className="mt-3 pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Payment Status: </span>
                  <span className={`text-sm font-medium px-2 py-1 rounded ${
                    movement.paymentStatus === 'PAID' ? 'bg-green-100 text-green-800' :
                    movement.paymentStatus === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {movement.paymentStatus}
                  </span>
                  {movement.paymentTerms && (
                    <span className="ml-3 text-sm text-gray-500">
                      Terms: {movement.paymentTerms.replace('_', ' ')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    } else if (movement.type === 'MULTI_ISSUE') {
      return (
        <tr className="bg-gray-50">
          <td colSpan="6" className="px-6 py-4">
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <FaPrescription className="mr-2 text-purple-600" />
                Prescribed Medicines:
              </h4>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-600 border-b">
                    <th className="text-left py-2">Medicine</th>
                    <th className="text-left py-2">Quantity</th>
                    <th className="text-left py-2">Unit Price</th>
                    <th className="text-left py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {movement.medicines.map((med, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="py-2">
                        {med.medicineName}
                        {med.dosage && (
                          <div className="text-xs text-gray-500">Dosage: {med.dosage}</div>
                        )}
                        {med.instructions && (
                          <div className="text-xs text-gray-500">{med.instructions}</div>
                        )}
                      </td>
                      <td className="py-2">{med.quantity}</td>
                      <td className="py-2">{formatCurrency(med.unitPrice)}</td>
                      <td className="py-2 font-medium">{formatCurrency(med.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-300 font-medium">
                    <td colSpan="3" className="py-2 text-right">Total:</td>
                    <td className="py-2">{formatCurrency(movement.totalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Stock Movements</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => handleAddMovement('SINGLE_ISSUE')}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <FaArrowUp />
            <span>Single Issue</span>
          </button>
          <button
            onClick={() => handleAddMovement('MULTI_ISSUE')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <FaPrescription />
            <span>Multi-Medicine Issue</span>
          </button>
          <button
            onClick={() => handleAddMovement('SINGLE_PURCHASE')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <FaArrowDown />
            <span>Single Purchase</span>
          </button>
          <button
            onClick={() => handleAddMovement('MULTI_PURCHASE')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <FaShoppingCart />
            <span>Multi-Medicine Purchase</span>
          </button>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="mb-4 flex space-x-2">
        <button
          onClick={() => setFilter('ALL')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === 'ALL' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilter('PURCHASE')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
            filter === 'PURCHASE' 
              ? 'bg-green-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <FaArrowDown />
          <span>Purchases</span>
        </button>
        <button
          onClick={() => setFilter('ISSUE')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
            filter === 'ISSUE' 
              ? 'bg-red-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <FaArrowUp />
          <span>Issues</span>
        </button>
      </div>

      {/* Stock Movements Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reference/Patient
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMovements.map((movement) => (
              <React.Fragment key={movement.id}>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(movement.createdAt || movement.issueDate || movement.purchaseDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getMovementIcon(movement.type)}
                      <span className={`px-2 py-1 text-xs rounded-full ${getMovementBadge(movement.type)}`}>
                        {getMovementLabel(movement.type)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {renderMovementDetails(movement)}
                  </td>
                  <td className="px-6 py-4">
                    {renderItemsCount(movement)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    {renderTotalAmount(movement)}
                  </td>
                  <td className="px-6 py-4">
                    {renderReferenceInfo(movement)}
                  </td>
                </tr>
                {expandedMovements[movement.id] && renderExpandedContent(movement)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {filteredMovements.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No stock movements found
          </div>
        )}
      </div>

      {/* Modal for Forms */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          modalType === 'MULTI_PURCHASE' ? 'Multi-Medicine Purchase' :
          modalType === 'MULTI_ISSUE' ? 'Multi-Medicine Issue' :
          modalType === 'SINGLE_PURCHASE' ? 'Record Stock Purchase' :
          'Record Stock Issue'
        }
        size="lg"
      >
        {modalType === 'MULTI_PURCHASE' && (
          <MultiMedicinePurchaseForm onClose={handleCloseModal} />
        )}
        {modalType === 'MULTI_ISSUE' && (
          <MultiMedicineIssueForm onClose={handleCloseModal} />
        )}
        {modalType === 'SINGLE_PURCHASE' && (
          <StockMovementForm onClose={handleCloseModal} />
        )}
        {modalType === 'SINGLE_ISSUE' && (
          <StockMovementForm onClose={handleCloseModal} />
        )}
      </Modal>
    </div>
  );
};

export default StockMovementList;